import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, Clock, CheckCircle, Heart, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function DigitalBoundariesGuide() {
  const navigate = useNavigate();

  const offlineActivities = [
    { activity: "Cook a meal from scratch", benefit: "Creative, sensory, produces something tangible" },
    { activity: "Call (don't text) a friend", benefit: "Real connection, hearing voices matters" },
    { activity: "Take a walk without headphones", benefit: "Process thoughts, notice surroundings" },
    { activity: "Read a physical book", benefit: "Deep focus, no notifications" },
    { activity: "Do a puzzle or craft", benefit: "Hands busy, mind calm" },
    { activity: "Garden or tend plants", benefit: "Nurturing, grounding, nature connection" },
    { activity: "Write by hand", benefit: "Slows thoughts, more intentional" },
    { activity: "Play a board game", benefit: "Social, fun, screen-free" }
  ];

  const boundaries = [
    { boundary: "No phones at meals", why: "Protects connection time and mindful eating" },
    { boundary: "Charge phone outside bedroom", why: "Improves sleep, reduces morning scrolling" },
    { boundary: "No work email after 7 PM", why: "Protects recovery and family time" },
    { boundary: "Social media time limit (30 min)", why: "Prevents endless scrolling" },
    { boundary: "One screen-free day/week", why: "Proves you can live without constant connection" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 6 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Digital Boundaries
          </h1>
          <p className="text-xl text-secondary-600">Reclaim Your Time and Attention</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Smartphone className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Challenge</h3>
              <p className="text-slate-300 text-lg">
                Choose a 3-hour window today to be completely offline. Turn off notifications, put devices away. 
                Use this time for real-world activities. Notice how it feels.
              </p>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Digital Boundaries Matter</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "Constant Alerts", desc: "Notifications trigger stress hormones every time" },
              { title: "Comparison Culture", desc: "Social media increases anxiety and depression" },
              { title: "Attention Fragmentation", desc: "Average person checks phone 96 times/day" },
              { title: "Sleep Disruption", desc: "Blue light and stimulation hurt sleep" },
              { title: "Relationship Strain", desc: "Phubbing (phone snubbing) damages connections" },
              { title: "Mental Exhaustion", desc: "Always-on culture depletes mental energy" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-slate-50 rounded-xl">
                <h3 className="font-bold text-slate-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Boundaries to Set */}
        <section className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-8 border border-slate-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Boundaries to Consider</h2>
          <div className="space-y-3">
            {boundaries.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-slate-700 mb-1 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-slate-500" />
                  {item.boundary}
                </h3>
                <p className="text-sm text-secondary-600 ml-6">{item.why}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Offline Activities */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-slate-500" />
            What to Do Instead
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {offlineActivities.map((item, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-xl">
                <h3 className="font-bold text-slate-700">{item.activity}</h3>
                <p className="text-sm text-secondary-600">{item.benefit}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default DigitalBoundariesGuide;
